import { Timestamp, GeoPoint } from 'firebase/firestore';
import type { DrinkFlavor, DrinkAttribute } from '../../types/firestore';

// Type definitions for Firestore utilities
export type LogFunction = (message: string) => void;

// Validation constants
export const VALID_FLAVOR_NAMES = [
  'Root Beer', 'Sarsaparilla', 'Birch Beer', 'Cream Soda', 'Cola', 
  'Ginger Ale', 'Ginger Beer', 'Fruit', 'Grape Soda', 'Lemon', 
  'Lime', 'Lemon-Lime', 'Citrus', 'Peach', 'Black Cherry', 
  'Cherry', 'Other Fruit', "Fruit (non-specific)", 'Other Flavor'
];

export const VALID_ATTRIBUTE_NAMES = [
  'Lo Cal/Diet/Low Sugar', 'Bottles', 'Cans', 'Draft / Fountain',
  'Caffeinated', 'THC infusion', 'Alcohol (contains)', 'Prebiotic',
  'Kombucha', 'Energy Drink'
];

export const VALID_BRAND_SIZES = ['craft', 'mass', 'unknown'];

// Add the missing constant
export const VALID_LOCATION_TYPES = ["restaurant", "shop", "brewery", "other"];

// List of all main collections in Firestore
export const COLLECTIONS = [
  'flavors',
  'attributes',
  'brands', 
  'drinks',
  'locations', 
  'sightings',
  'permissions',
  'reviews',
  'users',
  'usernames',
  'systemSettings'
];

// Fields to exclude during import (computed by Cloud Functions or purely denormalized)
export const EXCLUDED_FIELDS: Record<string, string[]> = {
  drinks: ['ratingSum', 'averageRating', 'reviewCount', 'sightingCount'],
  locations: ['averageRating', 'ratingCount', 'drinkCount', 'lastSightingDate'],
  sightings: ['confirmationCount', 'lastConfirmed'], // lastConfirmed is updated by trigger
  users: ['usernameLower', 'visitedLocationCount'], // Computed by Cloud Functions
  reviews: [], // Keep all fields during import
  visitedLocations: [], // Keep all fields during import
  confirmations: [], // Keep all fields during import
};

// Fields that are denormalized but need to be kept during import
// These will be used to resolve references
export const DENORMALIZED_FIELDS: Record<string, string[]> = {
  drinks: ['brandName'], // Keep for reference resolution
  sightings: ['locationName', 'drinkName', 'brandName'], // Keep for reference resolution
  reviews: ['username', 'drinkName', 'locationName'], // Keep for reference resolution
  visitedLocations: ['locationName'], // Keep for reference resolution
  confirmations: ['username'], // Keep for reference resolution
};

// Default values for missing fields
export interface DefaultValues {
  // Core collections
  flavors: {
    name: string;
    groupId: number;
    orderInGroup: number;
  };
  attributes: {
    name: string;
    groupId: number;
    orderInGroup: number;
  };
  brands: {
    name: string;
    brandSize: 'craft' | 'mass' | 'unknown';
  };
  drinks: {
    name: string;
    brandId: string;
    brandName: string;
    flavors: DrinkFlavor[];
    attributes: DrinkAttribute[];
  };
  locations: {
    name: string;
    address: string;
    url: string | null;
    locationType: "restaurant" | "shop" | "brewery" | "other";
    coordinates: GeoPoint;
    googlePlaceId: string | null;
    createdAt: Timestamp;
    updatedAt: Timestamp;
  };
  sightings: {
    locationId: string;
    locationName: string;
    drinkId: string;
    drinkName: string;
    brandId: string;
    firstSighted: Timestamp;
  };
  users: {
    username: string;
    permissions: string[];
    profilePictureUrl: string | null;
    uiTheme: string | null;
    createdAt: Timestamp;
    updatedAt: Timestamp | null;
    isAdmin: boolean;
    email: string | null;
  };
  permissions: {
    identifier: string;
    description: string;
  };
  reviews: {
    userId: string;
    username: string;
    drinkId: string;
    drinkName: string;
    rating: number;
    reviewText: string | null;
    isPublic: boolean;
    locationId: string | null;
    locationName: string | null;
    date: Timestamp;
  };
  systemSettings: {
    // Algolia document defaults
    algolia: {
      syncMode: 'normal' | 'importing' | 'disabled';
      incrementalSyncEnabled: boolean;
      lastFullSyncTime: Timestamp | null;
      lastUpdated: Timestamp | null;
    };
  };
  
  // Subcollections
  visitedLocations: {
    locationId: string;
    locationName: string;
    rating: number;
    publicNote: string | null;
    privateNote: string | null;
    visitDate: Timestamp;
  };
  confirmations: {
    timestamp: Timestamp;
    userId: string;
    username: string;
    createdAt: Timestamp;
  };
}

// Type for validation result
export interface ValidationResult {
  valid: boolean;
  issues: Array<{
    severity: 'error' | 'warning';
    message: string;
  }>;
} 